// src/components/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import { isAuthed, signOut } from '../auth';
import HamburgerMenu from './HamburgerMenu';
import logo from '../assets/logo.png';
import '../styles/theme.css';

export default function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const authed = isAuthed();
  const isHomePage = pathname === '/';
  const isDashboard = pathname === '/dashboard';
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleLogoClick = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
    setMenuOpen(false);
  };

  const handleSignOut = () => {
    signOut();
    navigate('/', { replace: true });
    setMenuOpen(false);
  };

  // Fallback handler for non-home routes: navigate to "/" with target section
  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    if (isHomePage) {
      // On home, react-scroll handles it via <ScrollLink />
      return;
    }

    // On another route → go home with scroll target in location.state
    navigate('/', { state: { scrollTo: sectionId } });
    setMenuOpen(false);
  };

  const closeMenu = () => setMenuOpen(false);

  // Scroll detection for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Nav links (same for desktop and mobile menu)
  const navLinks = isHomePage ? (
    <>
      <ScrollLink
        to="about"
        smooth={true}
        offset={-80}
        duration={500}
        className="nav-link"
        onClick={closeMenu}
      >
        About
      </ScrollLink>
      <ScrollLink
        to="pricing"
        smooth={true}
        offset={-80}
        duration={500}
        className="nav-link"
        onClick={closeMenu}
      >
        Pricing
      </ScrollLink>
    </>
  ) : (
    <>
      <a href="#about" onClick={(e) => handleNavClick(e, 'about')} className="nav-link">
        About
      </a>
      <a href="#pricing" onClick={(e) => handleNavClick(e, 'pricing')} className="nav-link">
        Pricing
      </a>
    </>
  );

  // Auth buttons
  const authButtons = authed ? (
    <>
      {pathname === '/dashboard' ? (
        <button type="button" className="btn btn--ghost" onClick={(e) => handleNavClick(e, 'pricing')}>
          Change plan
        </button>
      ) : (
        <Link to="/dashboard" className="btn btn--ghost" onClick={closeMenu}>
          Dashboard
        </Link>
      )}
      <button type="button" className="btn" onClick={handleSignOut}>
        Sign out
      </button>
    </>
  ) : (
    <>
      <Link to="/login" className="btn btn--ghost" onClick={closeMenu}>
        Log in
      </Link>
      <Link to="/register" className="btn" onClick={closeMenu}>
        Register
      </Link>
    </>
  );

  return (
    <header className={`site-head ${isScrolled ? 'site-head--scrolled' : ''} ${isDashboard ? 'site-head--dashboard' : ''}`}>
      <div className="container site-head-inner">
        <Link to="/" onClick={handleLogoClick} className="brand brand-link">
          <img src={logo} alt="Your Highest Self" className="brand-logo" />
          Your Highest Self
        </Link>

        {/* Desktop navigation - hidden on mobile/tablet */}
        <nav className="nav nav--desktop">
          {navLinks}
          {authButtons}
        </nav>

        {/* Mobile/Tablet navigation - everything in hamburger for clean, consistent UX */}
        <div className="nav nav--mobile">
          <HamburgerMenu isOpen={menuOpen} onToggle={() => setMenuOpen(!menuOpen)}>
            {navLinks}
            <div className="menu-divider"></div>
            {authButtons}
          </HamburgerMenu>
        </div>
      </div>
    </header>
  );
}
